import { useLayoutEffect, useRef, useState } from "react"

const scrollerOf = (node: HTMLElement): HTMLElement | null => {
  for (let parent = node.parentElement; parent; parent = parent.parentElement) {
    const { overflowY } = getComputedStyle(parent)
    if (overflowY === "auto" || overflowY === "scroll") return parent
  }
  return null
}

/**
 * Pins a block to its page's scroller exactly where it already rests.
 *
 * A stack of sticky blocks — a page title, a tab bar, a toolbar, a header row —
 * each stuck at its own resting height comes to rest one under the other with
 * none of them travelling: the first pixel of scroll moves the rows and nothing
 * else. Stuck at a guessed offset instead, each block would slide before it
 * caught.
 *
 * The resting height is read off a zero-height marker placed just before the
 * block, because the block itself is somewhere else once it is stuck. Place the
 * returned `markerRef` on an empty element immediately before the sticky one.
 * Returns `undefined` until it has measured, and while `enabled` is false.
 */
export const useStickAtRest = (enabled: boolean) => {
  const markerRef = useRef<HTMLSpanElement | null>(null)
  const [ top, setTop ] = useState<number | undefined>(undefined)

  useLayoutEffect(() => {
    const marker = markerRef.current
    if (!enabled || !marker) {
      setTop(undefined)
      return undefined
    }

    const scroller = scrollerOf(marker)
    if (!scroller) return undefined

    const measure = () => {
      const offset = marker.getBoundingClientRect().top
        - scroller.getBoundingClientRect().top
        + scroller.scrollTop
        - scroller.clientTop
      const next = Math.max(0, Math.round(offset * 2) / 2)
      setTop((held) => (held === next ? held : next))
    }

    measure()

    if (typeof ResizeObserver === "undefined") return undefined

    // Anything above the marker changing height moves where it rests.
    const observer = new ResizeObserver(measure)
    observer.observe(scroller)
    for (const child of Array.from(scroller.children)) observer.observe(child)

    return () => observer.disconnect()
  }, [ enabled ])

  return { markerRef, top }
}
